import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, MapPin, Clock, Instagram, ArrowUpRight, MessageCircle } from 'lucide-react';
import BookingModal from '../components/BookingModal';

const Contact = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>Contact Us - BLR Nail Club | Book Appointment Bangalore</title>
        <meta name="description" content="Contact BLR Nail Club for appointments. Locations in RR Nagar, Basaveshwarnagar, Sahakarnagar & CSK Mall Varthur. WhatsApp booking available. Open 7 days a week." />
        <meta name="keywords" content="contact BLR Nail Club, book nail appointment Bangalore, nail salon phone number, WhatsApp booking, salon location RR Nagar, Sahakarnagar nail salon" />
        <link rel="canonical" href="https://www.blrnailclub.in/contact" />
        <meta property="og:title" content="Contact & Book - BLR Nail Club Bangalore" />
        <meta property="og:description" content="Book your appointment at BLR Nail Club. Call +91 72046 02211 or visit our locations." />
        <meta property="og:url" content="https://www.blrnailclub.in/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact BLR Nail Club",
            "description": "Book your appointment or contact us",
            "url": "https://www.blrnailclub.in/contact",
            "mainEntity": {
              "@type": "BeautySalon",
              "name": "BLR Nail Club",
              "telephone": "+91-7204602211",
              "email": "blrnailclub@gmail.com"
            }
          })}
        </script>
      </Helmet>
      
      <main className="bg-black min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pb-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4 font-light">Get In Touch</p>
          <h1 className="text-white text-5xl md:text-6xl font-light tracking-wide mb-6">
            Contact & Book
          </h1>
          <p className="text-white/60 text-base md:text-lg font-light max-w-2xl mx-auto">
            Ready to experience the art of beauty? Visit us or book your appointment online. 
            We're here to make you shine.
          </p>
        </div>
      </section>

      {/* Contact Details Section */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Information */}
            <div>
              <h2 className="text-white text-3xl md:text-4xl font-light tracking-wide mb-10">
                Contact Information
              </h2>

              <div className="space-y-6">
                {/* Phone */}
                <a
                  href="tel:+917204602211"
                  className="flex items-start gap-5 group p-6 bg-black border border-white/10 hover:border-[#B8860B]/50 transition-colors"
                  data-testid="contact-page-phone"
                >
                  <div className="w-14 h-14 bg-[#B8860B] flex items-center justify-center flex-shrink-0">
                    <Phone strokeWidth={1} size={24} className="text-black" />
                  </div>
                  <div>
                    <p className="text-[#B8860B] text-xs tracking-[0.2em] uppercase mb-2">Phone</p>
                    <p className="text-white text-2xl font-light group-hover:text-[#B8860B] transition-colors">
                      +91 72046 02211
                    </p>
                    <p className="text-white/50 text-sm mt-1">Tap to call</p>
                  </div>
                </a>

                {/* Address */}
                <div
                  className="p-6 bg-black border border-white/10"
                  data-testid="contact-page-address"
                >
                  <div className="flex items-start gap-5 mb-4">
                    <div className="w-14 h-14 bg-[#B8860B] flex items-center justify-center flex-shrink-0">
                      <MapPin strokeWidth={1} size={24} className="text-black" />
                    </div>
                    <div>
                      <p className="text-[#B8860B] text-xs tracking-[0.2em] uppercase mb-2">Our Branches</p>
                      <p className="text-white text-xl font-light">4 Locations in Bangalore</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4 mt-6">
                    {/* RR Nagar */}
                    <div className="border-l-2 border-[#B8860B] pl-4">
                      <p className="text-white font-medium text-sm">RR Nagar</p>
                      <p className="text-white/60 text-xs mt-1">N2VK Arcade, 1154, Jawaharlal Nehru Rd</p>
                      <a href="tel:+917204602211" className="text-[#B8860B] text-xs hover:underline">+91 7204602211</a>
                    </div>
                    
                    {/* Basaveshwarnagar */}
                    <div className="border-l-2 border-[#B8860B] pl-4">
                      <p className="text-white font-medium text-sm">Basaveshwarnagar</p>
                      <p className="text-white/60 text-xs mt-1">LGF, No. 9, West of Chord Road</p>
                      <a href="tel:+919611560759" className="text-[#B8860B] text-xs hover:underline">+91 9611560759</a>
                    </div>
                    
                    {/* Sahakarnagar - NEW */}
                    <div className="border-l-2 border-[#B8860B] pl-4">
                      <p className="text-white font-medium text-sm">Sahakarnagar <span className="text-[#B8860B] text-[10px] ml-1">NEW</span></p>
                      <p className="text-white/60 text-xs mt-1">Ground Floor, 332, Sahakarnagar Main Rd</p>
                      <a href="tel:+917204604503" className="text-[#B8860B] text-xs hover:underline">+91 7204604503</a>
                    </div>
                    
                    {/* CSK Mall Gunjur */}
                    <div className="border-l-2 border-[#B8860B] pl-4">
                      <p className="text-white font-medium text-sm">CSK Mall Gunjur</p>
                      <p className="text-white/60 text-xs mt-1">Shop 50, 1st Floor, CSK Mall, Gunjur</p>
                      <a href="tel:+919741763377" className="text-[#B8860B] text-xs hover:underline">+91 9741763377</a>
                    </div>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-start gap-5 p-6 bg-black border border-white/10">
                  <div className="w-14 h-14 bg-[#B8860B] flex items-center justify-center flex-shrink-0">
                    <Clock strokeWidth={1} size={24} className="text-black" />
                  </div>
                  <div>
                    <p className="text-[#B8860B] text-xs tracking-[0.2em] uppercase mb-2">Business Hours</p>
                    <p className="text-white text-xl font-light">Opens 10 AM</p>
                    <p className="text-white/50 text-sm mt-1">Open all days</p>
                  </div>
                </div>

                {/* Instagram */}
                <a
                  href="https://www.instagram.com/blr_nailclub/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-5 group p-6 bg-black border border-white/10 hover:border-[#B8860B]/50 transition-colors"
                  data-testid="contact-page-instagram"
                >
                  <div className="w-14 h-14 bg-[#B8860B] flex items-center justify-center flex-shrink-0">
                    <Instagram strokeWidth={1} size={24} className="text-black" />
                  </div>
                  <div>
                    <p className="text-[#B8860B] text-xs tracking-[0.2em] uppercase mb-2">Instagram</p>
                    <p className="text-white text-xl font-light group-hover:text-[#B8860B] transition-colors">
                      @blr_nailclub
                    </p>
                    <p className="text-white/50 text-sm mt-1">Follow us for updates</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Booking Section */}
            <div>
              <h2 className="text-white text-3xl md:text-4xl font-light tracking-wide mb-10">
                Book Appointment
              </h2>

              {/* Booking Card */}
              <div className="bg-black border border-[#B8860B] p-8 md:p-10 mb-6">
                <h3 className="text-white text-2xl md:text-3xl font-light tracking-wide mb-4">
                  Ready to<br />
                  <span className="text-[#B8860B]">Transform?</span>
                </h3>
                <p className="text-white/60 font-light leading-relaxed mb-8">
                  Book your appointment online through our booking system. 
                  Choose your preferred service, date, and time.
                </p>
                <button
                  onClick={() => setIsBookingModalOpen(true)}
                  className="w-full bg-[#B8860B] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#9A7B0A] transition-colors flex items-center justify-center gap-3 group"
                  data-testid="contact-book-button"
                >
                  Book Online
                  <ArrowUpRight strokeWidth={1.5} size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </button>
              </div>

              {/* WhatsApp */}
              <a
                href="https://wa.me/917204602211?text=Hi!%20I'd%20like%20to%20book%20an%20appointment"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#25D366] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#20BD5A] transition-colors flex items-center justify-center gap-3 mb-6"
              >
                <MessageCircle size={20} strokeWidth={1.5} />
                Book via WhatsApp
              </a>

              {/* Alternative Contact */}
              <div className="bg-black border border-white/10 p-8">
                <h4 className="text-white text-xl font-light mb-4">Prefer to Call?</h4>
                <p className="text-white/50 font-light text-sm mb-6">
                  Our team is happy to assist you with booking over the phone. 
                  Call us during business hours.
                </p>
                <a
                  href="tel:+917204602211"
                  className="w-full border border-white/30 text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:border-[#B8860B] hover:text-[#B8860B] transition-colors flex items-center justify-center gap-3"
                >
                  <Phone strokeWidth={1.5} size={18} />
                  Call +91 72046 02211
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="py-16 md:py-24 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4 font-light">Find Us</p>
            <h2 className="text-white text-3xl md:text-4xl font-light tracking-wide">
              Our Location
            </h2>
          </div>

          <div className="border border-white/10" data-testid="google-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.5!2d77.5!3d12.92!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xe504940d6c77aead!2sBLR%20Nail%20club%20-%20RR%20nagar!5e0!3m2!1sen!2sin!4v1680000000000!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0, filter: 'grayscale(100%) invert(90%)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="BLR Nail Club Location"
            ></iframe>
          </div>

          <div className="text-center mt-8">
            <a
              href="https://maps.google.com/?q=BLR+Nail+club+-+RR+nagar"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-white/30 text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:border-[#B8860B] hover:text-[#B8860B] transition-colors"
              data-testid="get-directions-link"
            >
              <MapPin strokeWidth={1.5} size={18} />
              Get Directions
            </a>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
    </main>
    </>
  );
};

export default Contact;
